<template>
  <v-container class="d-flex justify-center align-center min-height-screen">
    <v-card class="verify-email-card" max-width="600" elevation="8" rounded="xl">
      <v-card-title class="text-h4 font-weight-regular text-center py-8">
        <v-icon 
          left 
          size="32" 
          :color="verificationStatus === 'success' ? '#4CAF50' : '#F47820'" 
          class="mr-3"
        >
          <!-- <i :class="getStatusIcon()"></i> -->
        </v-icon>
        Email Verification
      </v-card-title>

      <v-card-text class="px-8 pb-8">
        <!-- Loading State -->
        <div v-if="verificationStatus === 'loading'" class="text-center">
          <v-progress-circular 
            indeterminate 
            color="#F47820" 
            size="64" 
            class="mb-4"
          ></v-progress-circular>
          <p class="text-h6 mb-4">Verifying your email...</p>
          <p class="text-body-1 text-medium-emphasis">
            Please wait while we verify your email address.
          </p>
        </div>

        <!-- Success State -->
        <div v-else-if="verificationStatus === 'success'" class="text-center">
          <v-icon color="success" size="64" class="mb-4">
            <i class="fas fa-check-circle"></i>
          </v-icon>
          <p class="text-h6 mb-4 text-success">Email Successfully Verified!</p>
          <p class="text-body-1 text-medium-emphasis mb-6">
            Your email address has been successfully verified.
          </p>
          <!-- <v-btn 
            color="#F47820" 
            variant="elevated" 
            size="large" 
            @click="redirectToLogin"
            class="text-white"
          >
            <v-icon left>
              <i class="fas fa-sign-in-alt"></i>
            </v-icon>
            Go to Login
          </v-btn> -->
        </div>

        <!-- Error State -->
        <div v-else-if="verificationStatus === 'error'" class="text-center">
          <v-icon color="error" size="64" class="mb-4">
            <i class="fas fa-exclamation-triangle"></i>
          </v-icon>
          <p class="text-h6 mb-4 text-error">Verification Failed</p>
          <p class="text-body-1 text-medium-emphasis mb-6">
            {{ errorMessage }}
          </p>
          <div class="d-flex flex-column gap-3">
            <v-btn 
              color="#F47820" 
              variant="elevated" 
              size="large" 
              @click="retryVerification"
              class="text-white"
            >
              <v-icon left>
                <i class="fas fa-redo"></i>
              </v-icon>
              Retry Verification
            </v-btn>
            <!-- <v-btn 
              color="grey" 
              variant="outlined" 
              size="large" 
              @click="redirectToLogin"
            >
              <v-icon left>
                <i class="fas fa-home"></i>
              </v-icon>
              Back to Login
            </v-btn> -->
          </div>
        </div>

        <!-- Token Missing State -->
        <div v-else-if="verificationStatus === 'no-token'" class="text-center">
          <v-icon color="warning" size="64" class="mb-4">
            <i class="fas fa-question-circle"></i>
          </v-icon>
          <p class="text-h6 mb-4 text-warning">Invalid Verification Link</p>
          <p class="text-body-1 text-medium-emphasis mb-6">
            The verification link appears to be invalid or incomplete. Please check your email for the correct link.
          </p>
          <v-btn 
            color="grey" 
            variant="outlined" 
            size="large" 
            @click="redirectToLogin"
          >
            <v-icon left>
              <i class="fas fa-home"></i>
            </v-icon>
            Back to Login
          </v-btn>
        </div>

        <!-- Details Card (for debugging/admin) -->
        <v-card 
          v-if="showDetails && verificationDetails" 
          variant="outlined" 
          class="mt-6 pa-4"
        >
          <v-card-subtitle class="text-body-2 font-weight-medium mb-2">
            <v-icon left size="16" color="#F47820">
              <i class="fas fa-info-circle"></i>
            </v-icon>
            Verification Details
          </v-card-subtitle>
          <div class="text-caption">
            <p><strong>Email:</strong> {{ verificationDetails.email }}</p>
            <p><strong>Verified At:</strong> {{ verificationDetails.verified_at }}</p>
            <p v-if="verificationDetails.expires_at"><strong>Token Expires:</strong> {{ verificationDetails.expires_at }}</p>
          </div>
        </v-card>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import api from "../api/api";
import { flashMessage } from "../functions.js";
</script>

<script>
export default {
  name: "VerifyEmail",
  data: () => ({
    verificationStatus: 'loading', // 'loading', 'success', 'error', 'no-token'
    errorMessage: '',
    verificationDetails: null,
    showDetails: false,
    token: null
  }),
  methods: {
    getStatusIcon() {
      switch (this.verificationStatus) {
        case 'loading':
          return 'fas fa-spinner fa-spin';
        case 'success':
          return 'fas fa-check-circle';
        case 'error':
          return 'fas fa-exclamation-triangle';
        case 'no-token':
          return 'fas fa-question-circle';
        default:
          return 'fas fa-envelope';
      }
    },

    async verifyEmail() {
      try {
        console.log('Starting email verification with token:', this.token);
        
        const response = await api.post('/verify-email', {
          token: this.token
        }).catch(error => {
          // Handle 500 errors specifically
          if (error.response && error.response.status === 500) {
            throw {
              response: {
                data: {
                  message: 'Invalid or expired verification token',
                  error: 'Invalid or expired verification token'
                }
              }
            };
          }
          throw error; // Re-throw other errors
        });

        console.log('Verification response:', response.data);

        if (response.data.success) {
          this.verificationStatus = 'success';
          this.verificationDetails = response.data.details || null;
          this.showDetails = !!response.data.details;
          
          // Show success message with the verified email
          flashMessage(
            this.$flashMessage, 
            '#4CAF50', 
            '#ffffff', 
            response.data.message || 'Email successfully verified!'
          );
        } else {
          this.verificationStatus = 'error';
          this.errorMessage = response.data.message || 'Verification failed. Please try again.';
          
          // Show error message
          flashMessage(
            this.$flashMessage, 
            '#F44336', 
            '#ffffff', 
            this.errorMessage
          );
        }
      } catch (error) {
        console.error('Email verification error:', error);
        this.verificationStatus = 'error';
        
        // Handle specific error messages
        if (error.response?.data?.message) {
          this.errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          this.errorMessage = error.response.data.error;
        } else if (error.message === 'Network Error') {
          this.errorMessage = 'Network error. Please check your internet connection.';
        } else {
          this.errorMessage = 'An error occurred during verification. Please try again.';
        }
        
        // Ensure we show "Invalid or expired verification token" for 500 errors
        if (error.response?.status === 500) {
          this.errorMessage = 'Invalid or expired verification token';
        }
        
        // Show error message
        flashMessage(
          this.$flashMessage, 
          '#F44336', 
          '#ffffff', 
          this.errorMessage
        );
      }
    },

    retryVerification() {
      this.verificationStatus = 'loading';
      this.errorMessage = '';
      this.verificationDetails = null;
      this.showDetails = false;
      
      // Add small delay for better UX
      setTimeout(() => {
        this.verifyEmail();
      }, 500);
    },

    redirectToLogin() {
      this.$router.push('/login');
    }
  },

  async mounted() {
    // Get token from URL query parameters
    this.token = this.$route.query.token;
    
    console.log('Token from URL:', this.token);
    
    if (!this.token) {
      console.error('No token found in URL');
      this.verificationStatus = 'no-token';
      return;
    }

    // Start verification process
    await this.verifyEmail();
  }
};
</script>

<style scoped>
.min-height-screen {
  min-height: 80vh;
}

.verify-email-card {
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.95) !important;
}

/* Custom styling for better visual appeal */
.v-card-title {
  background: #4F4F4F;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
}

/* Animation for loading spinner */
.fa-spin {
  animation: spin 2s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Enhanced focus states */
.v-btn:focus {
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Success state styling */
.text-success {
  color: #4CAF50 !important;
}

/* Error state styling */
.text-error {
  color: #F44336 !important;
}

/* Warning state styling */
.text-warning {
  color: #FF9800 !important;
}

/* Smooth transitions */
.v-icon, .v-btn {
  transition: all 0.3s ease;
}

/* Gap utility for button spacing */
.gap-3 > *:not(:last-child) {
  margin-bottom: 12px;
}
</style>